import React, { useState, useEffect, useMemo } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import {
  AiOutlineDashboard,
  AiOutlineUser,
  AiOutlineUserAdd,
  AiOutlineLogout,
} from "react-icons/ai";
import { FaUsers, FaWhatsapp, FaFacebook, FaTiktok } from "react-icons/fa";
import { IoIosNotifications } from "react-icons/io";
import { Layout, Menu, theme, Badge } from "antd";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import { useMediaQuery } from "react-responsive";

import logo from "../assets/Images/car-logo.png";
import {
  getLeads,
  getWhatsappChats,
  getAgentWhatsappChats,
} from "../features/customers/customerSlice";

const { Header, Sider, Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeMenuKey, setActiveMenuKey] = useState("dashboard");
  const [openKeys, setOpenKeys] = useState([]);
  const isDesktop = useMediaQuery({ minWidth: 785 });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const userRole = storedUser?.role;
  const agentName = storedUser?.agentName;

  const socket = io("http://localhost:8805", {
    withCredentials: true,
  });

  useEffect(() => {
    if (!isDesktop) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    dispatch(getLeads());

    if (userRole === "admin") {
      dispatch(getWhatsappChats());
    }

    if (userRole === "employee") {
      dispatch(getAgentWhatsappChats());
    }

    socket.on("lead:new", () => {
      dispatch(getLeads());

      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    socket.on("lead:updated", () => {
      dispatch(getLeads());

      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    socket.on("lead:deleted", () => {
      dispatch(getLeads());

      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    socket.on("lead:read", () => {
      dispatch(getLeads());

      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    socket.on("whatsapp:new", () => {
      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    socket.on("whatsapp:read", () => {
      if (userRole === "admin") {
        dispatch(getWhatsappChats());
      }

      if (userRole === "employee") {
        dispatch(getAgentWhatsappChats());
      }
    });

    return () => {
      socket.off("lead:new");
      socket.off("lead:updated");
      socket.off("lead:deleted");
      socket.off("lead:read");
      socket.off("whatsapp:new");
      socket.off("whatsapp:read");
    };
  }, [dispatch, userRole]);

  const leads = useSelector((state) => state?.customer?.leads || []);
  const whatsappChats = useSelector(
    (state) => state?.customer?.whatsappChats || []
  );

  const scopedLeads = useMemo(() => {
    if (userRole === "employee") {
      return leads.filter((lead) => lead.agentName === agentName);
    }
    return leads;
  }, [leads, userRole, agentName]);

  const whatsappLeadsCount = useMemo(() => {
    return scopedLeads.filter(
      (lead) => lead.source === "whatsapp" && lead.status === "new"
    ).length;
  }, [scopedLeads]);

  const facebookLeadsCount = useMemo(() => {
    return scopedLeads.filter(
      (lead) => lead.source === "facebook_form" && lead.status === "new"
    ).length;
  }, [scopedLeads]);

  const unreadLeadsCount = useMemo(() => {
    return scopedLeads.filter((lead) => lead.status === "new").length;
  }, [scopedLeads]);

  const unreadWhatsappCount = useMemo(() => {
    return whatsappChats.reduce(
      (sum, chat) => sum + Number(chat.unreadCount || 0),
      0
    );
  }, [whatsappChats]);

  const totalNotifications = unreadLeadsCount + unreadWhatsappCount;

  const selectedKeyFromRoute = useMemo(() => {
    if (
      location.pathname === "/admin" ||
      location.pathname === "/admin/" ||
      location.pathname.startsWith("/admin/dashboard")
    ) {
      return userRole === "employee" ? "my-leads" : "dashboard";
    }

    if (location.pathname.startsWith("/admin/my-leads")) return "my-leads";
    if (location.pathname.startsWith("/admin/leads/whatsapp"))
      return "whatsapp-leads";
    if (location.pathname.startsWith("/admin/leads/facebook"))
      return "facebook-leads";
    if (location.pathname.startsWith("/admin/whatsapp")) return "whatsapp";
    if (location.pathname.startsWith("/admin/agent-whatsapp"))
      return "agent-whatsapp";
    if (location.pathname.startsWith("/admin/team")) return "team";
    if (location.pathname.startsWith("/admin/add-team")) return "add-team";
    if (location.pathname.startsWith("/admin/active-campaigns"))
      return "active-campaigns";

    return userRole === "employee" ? "my-leads" : "dashboard";
  }, [location.pathname, userRole]);

  useEffect(() => {
    setActiveMenuKey(selectedKeyFromRoute);

    if (
      selectedKeyFromRoute === "whatsapp-leads" ||
      selectedKeyFromRoute === "facebook-leads"
    ) {
      setOpenKeys(["leads"]);
    }
  }, [selectedKeyFromRoute]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const renderMenuLabel = (label, count = 0) => (
    <div className="menu-label-wrapper">
      <span>{label}</span>
      {count > 0 && <span className="sidebar-notification-dot" />}
    </div>
  );

  const mobileLabelMap = {
    "my-leads": "My Leads",
    "active-campaigns": "Campaigns",
    whatsapp: "WhatsApp",
    "agent-whatsapp": "WhatsApp",
    signout: "Sign Out",
    dashboard: "Dashboard",
    team: "Team",
    "whatsapp-leads": "WA Leads",
    "facebook-leads": "FB Leads",
  };

  const adminItems = [
    {
      key: "dashboard",
      icon: <AiOutlineDashboard className="fs-4" />,
      label: renderMenuLabel("Dashboard"),
    },
   
        
    {
      key: "leads",
      icon: <AiOutlineUser className="fs-4" />,
      label: renderMenuLabel("Leads", unreadLeadsCount),
      children: [
        {
          key: "whatsapp-leads",
          icon: <FaWhatsapp className="fs-4" />,
          label: renderMenuLabel("WhatsApp Leads", whatsappLeadsCount),
        },
        {
          key: "facebook-leads",
          icon: <FaFacebook className="fs-4" />,
          label: renderMenuLabel("Facebook Leads", facebookLeadsCount),
        },
         {
          key: "leads/tiktok",
          icon: <FaTiktok className="fs-4" />,
          label: renderMenuLabel("Tiktok Leads", facebookLeadsCount),
        },
      ],
    },
    {
      key: "whatsapp",
      icon: <FaWhatsapp className="fs-4" />,
      label: renderMenuLabel("WhatsApp", unreadWhatsappCount),
    },
    {
      key: "team",
      icon: <FaUsers className="fs-4" />,
      label: renderMenuLabel("Team"),
    },
    {
      key: "add-team",
      icon: <AiOutlineUserAdd className="fs-4" />,
      label: renderMenuLabel("Add Team"),
    },
    {
      key: "signout",
      icon: <AiOutlineLogout className="fs-4" />,
      label: "Sign Out",
    },
  ];

  const employeeItems = [
    {
      key: "active-campaigns",
      icon: <AiOutlineDashboard className="fs-4" />,
      label: renderMenuLabel("Campaigns"),
    },
    {
      key: "agent-whatsapp",
      icon: <FaWhatsapp className="fs-4" />,
      label: renderMenuLabel("WhatsApp", unreadWhatsappCount),
    },
    {
      key: "my-leads",
      icon: <AiOutlineUser className="fs-4" />,
      label: renderMenuLabel("My Leads", unreadLeadsCount),
    },
    {
      key: "signout",
      icon: <AiOutlineLogout className="fs-4" />,
      label: "Sign Out",
    },
  ];

  const menuItems =
    userRole === "admin"
      ? adminItems
      : userRole === "employee"
      ? employeeItems
      : [];

  const handleMenuClick = ({ key }) => {
    setActiveMenuKey(key);

    if (key === "signout") {
      localStorage.clear();
      window.location.reload();
      return;
    }

    if (key === "dashboard") {
      navigate("/admin/dashboard");
      return;
    }

    if (key === "whatsapp-leads") {
      navigate("/admin/leads/whatsapp");
      return;
    }

    if (key === "facebook-leads") {
      navigate("/admin/leads/facebook");
      return;
    }

    if (key === "whatsapp") {
      navigate("/admin/whatsapp");
      return;
    }

    if (key === "agent-whatsapp") {
      navigate("/admin/agent-whatsapp");
      return;
    }

    navigate(`/admin/${key}`);
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isDesktop && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="main-sider"
        >
          <div className="logo">
            <h2 className="text-white fs-5 text-center py-3 mb-0">
              <span className="sm-logo">C.A</span>
              <span className="lg-logo">Carman Autos</span>
            </h2>
          </div>

          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[activeMenuKey]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            className="main-sidebar-menu"
            onClick={handleMenuClick}
            items={menuItems}
          />
        </Sider>
      )}

      <Layout>
        <div
          className="d-flex justify-content-between main-layout-header"
          style={{ padding: 0, background: colorBgContainer }}
        >
          {isDesktop && (
            <>
              {React.createElement(
                collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
                {
                  className: "trigger",
                  onClick: () => setCollapsed(!collapsed),
                }
              )}
            </>
          )}

          <div
            className={`d-flex gap-4 align-items-center ${
              !isDesktop
                ? "flex-row-reverse w-100 justify-content-between px-2"
                : ""
            }`}
          >
            <div className="position-relative top-notification-wrapper">
              <Badge
                count={totalNotifications}
                size="small"
                offset={[-2, 6]}
                color="#f47e20"
              >
                <IoIosNotifications className="fs-4 top-notification-icon" />
              </Badge>
            </div>

            <div className="d-flex gap-3 align-items-center dropdown">
              <div className="profile-avatar-wrapper">
                <img
                  width={38}
                  height={38}
                  src={logo}
                  alt="logo"
                  className="profile-avatar-image"
                />
                <span className="profile-active-dot"></span>
              </div>

              <div className="name-wrapper" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                <h5 className="mb-0">
                  {storedUser?.firstname || "Carman Autos"}
                </h5>
                {isDesktop && (
                  <p className="mb-0">
                    {storedUser?.email || "info@carmanautos.com"}
                  </p>
                )}
              </div>

              <div className="dropdown-menu">
                <li>
                  <Link
                    className="dropdown-item"
                    to="/"
                    onClick={() => {
                      localStorage.clear();
                    }}
                  >
                    Signout
                  </Link>
                </li>
              </div>
            </div>
          </div>
        </div>

      <Content
        className="main-layout-content"
        style={{
          margin: isDesktop
            ? "24px 16px"
            : "150px 8px 20px", // 👈 push down below fixed header
          padding: isDesktop ? 24 : 10,
          minHeight: 280,
          background: colorBgContainer,
        }}
      >
          <ToastContainer
            position="top-right"
            autoClose={250}
            newestOnTop
            theme="light"
          />
          <Outlet />
        </Content>
      </Layout>

      {!isDesktop && (
        <div className="mobile-bottom-nav">
          {userRole === "admin" ? (
            <>
              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "dashboard" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "dashboard" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <AiOutlineDashboard className="fs-4" />
                </div>
                <span className="mobile-bottom-nav-label">Dashboard</span>
              </button>

              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "whatsapp-leads" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "whatsapp-leads" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <AiOutlineUser className="fs-4" />
                  {whatsappLeadsCount > 0 && (
                    <span className="mobile-bottom-nav-dot" />
                  )}
                </div>
                <span className="mobile-bottom-nav-label">WA Leads</span>
              </button>

              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "facebook-leads" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "facebook-leads" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <FaFacebook className="fs-4" />
                  {facebookLeadsCount > 0 && (
                    <span className="mobile-bottom-nav-dot" />
                  )}
                </div>
                <span className="mobile-bottom-nav-label">FB Leads</span>
              </button>
              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "facebook-leads" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "facebook-leads" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <FaFacebook className="fs-4" />
                  {facebookLeadsCount > 0 && (
                    <span className="mobile-bottom-nav-dot" />
                  )}
                </div>
                <span className="mobile-bottom-nav-label">Tiktok</span>
              </button>

              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "whatsapp" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "whatsapp" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <FaWhatsapp className="fs-4" />
                  {unreadWhatsappCount > 0 && (
                    <span className="mobile-bottom-nav-dot" />
                  )}
                </div>
                <span className="mobile-bottom-nav-label">WhatsApp</span>
              </button>

              <button
                type="button"
                className={`mobile-bottom-nav-item ${
                  activeMenuKey === "team" ? "active" : ""
                }`}
                onClick={() => handleMenuClick({ key: "team" })}
              >
                <div className="mobile-bottom-nav-icon-wrapper">
                  <FaUsers className="fs-4" />
                </div>
                <span className="mobile-bottom-nav-label">Team</span>
              </button>
            </>
          ) : (
            employeeItems.map((item) => {
              const isActive = activeMenuKey === item.key;

              return (
                <button
                  key={item.key}
                  type="button"
                  className={`mobile-bottom-nav-item ${isActive ? "active" : ""}`}
                  onClick={() => handleMenuClick({ key: item.key })}
                >
                  <div className="mobile-bottom-nav-icon-wrapper">
                    {item.icon}
                    {item.key === "my-leads" && unreadLeadsCount > 0 && (
                      <span className="mobile-bottom-nav-dot" />
                    )}
                    {item.key === "agent-whatsapp" &&
                      unreadWhatsappCount > 0 && (
                        <span className="mobile-bottom-nav-dot" />
                      )}
                  </div>

                  <span className="mobile-bottom-nav-label">
                    {mobileLabelMap[item.key] || item.key}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </Layout>
  );
};

export default MainLayout;